import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowDown, ArrowUp, UserCheck } from 'lucide-react';

import { adminContent, media } from 'services/api';
import type { AdminInspireDeck } from 'services/api/adminContentClient';
import type {
  ChecklistItemTemplate,
  ChecklistItemTemplateGroup,
  CohortChecklistAssignments,
  CohortMember,
  CohortMemberWriteResponse,
  CohortRole,
  CohortSummary,
  ContentDefaultsResponse,
  AdminUserSummary,
  OrganizationSummary,
  MediaAsset,
  MediaAssetType,
} from 'types';
import ContentAssignmentEditor, {
  type DraftCourseAssignment,
  type DraftFlowAssignment,
} from 'components/admin/ContentAssignmentEditor';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Textarea } from 'components/ui/textarea';
import { Label } from 'components/ui/label';
import { LoadingAnimation } from 'components/ui/loading-animation';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'components/ui/tabs';
import { Checkbox } from 'components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { useToast } from 'components/ui/use-toast';
import { useAuth } from 'contexts/AuthContext';
import { cn } from 'lib/utils';
import { getCohortRoleLabel, getInviteeBucketLabel, lowerLabel } from 'utils/cohortRoleLabels';

type TemplateDraft = {
  id?: string;
  title: string;
  description: string;
  due_offset_days: string;
  external_url: string;
  is_active: boolean;
  media: MediaAsset | null;
  media_asset_id: string | null;
  question_flow_id: string;
};

type GroupDraft = {
  id?: string;
  name: string;
  description: string;
  template_ids: string[];
};

type CohortDraft = {
  id?: string;
  name: string;
  description: string;
  org_id: string;
  mentee_label_singular: string;
  mentee_label_plural: string;
  mentor_label_singular: string;
  mentor_label_plural: string;
  start_date: string;
  end_date: string;
  is_active: boolean;
};

type AssignmentRole = 'mentor' | 'mentee';

type FlowSummary = {
  id: number;
  name: string;
  description?: string;
  is_active?: boolean;
};

type CohortToolTab = 'cohorts' | 'templates' | 'groups';

const NO_FLOW_VALUE = 'none';
const ALL_ORGANIZATIONS_VALUE = 'all';
const COHORT_TOOL_TABS: CohortToolTab[] = ['cohorts', 'templates', 'groups'];

const EMPTY_TEMPLATE: TemplateDraft = {
  title: '',
  description: '',
  due_offset_days: '',
  external_url: '',
  is_active: true,
  media: null,
  media_asset_id: null,
  question_flow_id: NO_FLOW_VALUE,
};

const EMPTY_GROUP: GroupDraft = {
  name: '',
  description: '',
  template_ids: [],
};

const EMPTY_COHORT: CohortDraft = {
  name: '',
  description: '',
  org_id: '',
  mentee_label_singular: 'Scholar',
  mentee_label_plural: 'Scholars',
  mentor_label_singular: 'Mentor',
  mentor_label_plural: 'Mentors',
  start_date: '',
  end_date: '',
  is_active: true,
};

const EMPTY_ASSIGNMENTS: CohortChecklistAssignments = {
  mentor: { template_ids: [], template_group_ids: [] },
  mentee: { template_ids: [], template_group_ids: [] },
};

const EMPTY_CONTENT_DEFAULTS: ContentDefaultsResponse = {
  course_assignments: [],
  flow_assignments: [],
  override_org_course_defaults: false,
  override_org_flow_defaults: false,
  override_org_inspire_defaults: false,
  inspire_deck_assignments: [],
};

const normalizeList = <T,>(payload: any): T[] => {
  if (Array.isArray(payload)) {
    return payload as T[];
  }
  if (payload?.results && Array.isArray(payload.results)) {
    return payload.results as T[];
  }
  return [];
};

const extractApiErrorMessage = (error: any, fallback: string): string => {
  const data = error?.response?.data;
  if (typeof data === 'string' && data.trim()) return data;
  if (typeof data?.detail === 'string' && data.detail.trim()) return data.detail;
  if (typeof data?.error === 'string' && data.error.trim()) return data.error;
  if (typeof data?.message === 'string' && data.message.trim()) return data.message;
  if (Array.isArray(data?.non_field_errors) && data.non_field_errors.length) {
    return data.non_field_errors.filter((value: unknown) => typeof value === 'string').join(' ');
  }

  if (data && typeof data === 'object') {
    for (const value of Object.values(data as Record<string, unknown>)) {
      if (typeof value === 'string' && value.trim()) return value;
      if (Array.isArray(value)) {
        const firstString = value.find((item): item is string => typeof item === 'string' && item.trim().length > 0);
        if (firstString) return firstString;
      }
    }
  }

  if (typeof error?.message === 'string' && error.message.trim()) return error.message;
  return fallback;
};

const extractErrorText = (value: unknown): string | null => {
  if (typeof value === 'string' && value.trim()) return value.trim();
  if (Array.isArray(value)) {
    for (const entry of value) {
      const message = extractErrorText(entry);
      if (message) return message;
    }
  }
  if (value && typeof value === 'object') {
    for (const entry of Object.values(value as Record<string, unknown>)) {
      const message = extractErrorText(entry);
      if (message) return message;
    }
  }
  return null;
};

const findApiFieldError = (value: unknown, fieldName: string): string | null => {
  if (Array.isArray(value)) {
    for (const entry of value) {
      const message = findApiFieldError(entry, fieldName);
      if (message) return message;
    }
    return null;
  }
  if (!value || typeof value !== 'object') return null;

  const record = value as Record<string, unknown>;
  if (Object.prototype.hasOwnProperty.call(record, fieldName)) {
    const message = extractErrorText(record[fieldName]);
    if (message) return message;
  }
  for (const entry of Object.values(record)) {
    const message = findApiFieldError(entry, fieldName);
    if (message) return message;
  }
  return null;
};

const extractApiFieldErrorMessage = (error: any, fieldNames: string[]): string | null => {
  const data = error?.response?.data;
  for (const fieldName of fieldNames) {
    const message = findApiFieldError(data, fieldName);
    if (message) return message;
  }
  return null;
};

const getCohortDateValidationError = (startDate: string, endDate: string): string | null =>
  startDate && endDate && endDate < startDate
    ? 'End date must be on or after the start date.'
    : null;

type CohortDateFieldError = {
  message: string;
  source: 'range' | 'server';
};

type CohortDateErrors = {
  start_date: CohortDateFieldError | null;
  end_date: CohortDateFieldError | null;
};

const EMPTY_COHORT_DATE_ERRORS: CohortDateErrors = {
  start_date: null,
  end_date: null,
};

const normalizeExternalUrl = (value: string): string | null => {
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (/^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
};

const toMediaType = (value?: string | null): MediaAssetType => {
  if (value === 'image' || value === 'video' || value === 'pdf' || value === 'audio') {
    return value;
  }
  return 'other';
};

const templateToDraft = (template: ChecklistItemTemplate): TemplateDraft => ({
  id: template.id,
  title: template.title ?? '',
  description: template.description ?? '',
  due_offset_days:
    typeof template.due_offset_days === 'number' ? String(template.due_offset_days) : '',
  external_url: template.external_url ?? '',
  is_active: Boolean(template.is_active),
  media: template.media ?? null,
  media_asset_id: template.media_asset_id ?? null,
  question_flow_id: template.question_flow ? String(template.question_flow) : NO_FLOW_VALUE,
});

const groupToDraft = (group: ChecklistItemTemplateGroup): GroupDraft => ({
  id: group.id,
  name: group.name ?? '',
  description: group.description ?? '',
  template_ids:
    group.template_ids ??
    (group.templates ? group.templates.map((template) => template.id) : []),
});

const cohortToDraft = (cohort: CohortSummary): CohortDraft => ({
  id: cohort.id,
  name: cohort.name ?? '',
  description: cohort.description ?? '',
  org_id: cohort.organization?.org_id ?? '',
  mentee_label_singular: cohort.mentee_label_singular ?? 'Scholar',
  mentee_label_plural: cohort.mentee_label_plural ?? 'Scholars',
  mentor_label_singular: cohort.mentor_label_singular ?? 'Mentor',
  mentor_label_plural: cohort.mentor_label_plural ?? 'Mentors',
  start_date: cohort.start_date ?? '',
  end_date: cohort.end_date ?? '',
  is_active: Boolean(cohort.is_active),
});

const MentorshipTools: React.FC = () => {
  const { addToast } = useToast();
  const { user: currentUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [templates, setTemplates] = useState<ChecklistItemTemplate[]>([]);
  const [groups, setGroups] = useState<ChecklistItemTemplateGroup[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [loadingGroups, setLoadingGroups] = useState(true);
  const [loadingFlows, setLoadingFlows] = useState(true);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [mediaError, setMediaError] = useState('');
  const [templateDraft, setTemplateDraft] = useState<TemplateDraft>(EMPTY_TEMPLATE);
  const [groupDraft, setGroupDraft] = useState<GroupDraft>(EMPTY_GROUP);
  const [cohortDraft, setCohortDraft] = useState<CohortDraft>(EMPTY_COHORT);
  const [cohortDateErrors, setCohortDateErrors] =
    useState<CohortDateErrors>(EMPTY_COHORT_DATE_ERRORS);
  const [isSavingTemplate, setIsSavingTemplate] = useState(false);
  const [isSavingGroup, setIsSavingGroup] = useState(false);
  const [isSavingCohort, setIsSavingCohort] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [selectedCohortId, setSelectedCohortId] = useState<string | null>(null);
  const selectedCohortIdRef = useRef<string | null>(null);
  const cohortDateRangeValidationActiveRef = useRef(false);
  const [templateSearchTerm, setTemplateSearchTerm] = useState('');
  const [groupSearchTerm, setGroupSearchTerm] = useState('');
  const [cohortSearchTerm, setCohortSearchTerm] = useState('');
  const [cohortOrganizationFilter, setCohortOrganizationFilter] = useState<string>(ALL_ORGANIZATIONS_VALUE);
  const [flowSearchTerm, setFlowSearchTerm] = useState('');
  const [isFlowSelectOpen, setIsFlowSelectOpen] = useState(false);
  const [cohorts, setCohorts] = useState<CohortSummary[]>([]);
  const [organizations, setOrganizations] = useState<OrganizationSummary[]>([]);
  const [flows, setFlows] = useState<FlowSummary[]>([]);
  const flowsRequestRef = useRef(0);
  const [cohortMembers, setCohortMembers] = useState<CohortMember[]>([]);
  const [cohortAssignments, setCohortAssignments] =
    useState<CohortChecklistAssignments>(EMPTY_ASSIGNMENTS);
  const [cohortContentDefaults, setCohortContentDefaults] =
    useState<ContentDefaultsResponse>(EMPTY_CONTENT_DEFAULTS);
  const [draftDefaultCourses, setDraftDefaultCourses] = useState<DraftCourseAssignment[]>([]);
  const [draftDefaultFlows, setDraftDefaultFlows] = useState<DraftFlowAssignment[]>([]);
  const [availableInspireDecks, setAvailableInspireDecks] = useState<AdminInspireDeck[]>([]);
  const [draftInspireDeckIds, setDraftInspireDeckIds] = useState<string[]>([]);
  const [isLoadingCohortContentDefaults, setIsLoadingCohortContentDefaults] = useState(false);
  const [isSavingDefaultCourses, setIsSavingDefaultCourses] = useState(false);
  const [isSavingDefaultFlows, setIsSavingDefaultFlows] = useState(false);
  const [isSavingDefaultOverrides, setIsSavingDefaultOverrides] = useState(false);
  const [isSavingInspireDefaults, setIsSavingInspireDefaults] = useState(false);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [userSearchResults, setUserSearchResults] = useState<AdminUserSummary[]>([]);
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const [selectedUserRole, setSelectedUserRole] = useState<CohortRole>('mentee');
  const [staffJoinRole, setStaffJoinRole] = useState<CohortRole>('cohort_admin');
  const [staffJoinAsCurrent, setStaffJoinAsCurrent] = useState(false);
  const [applyToExistingMentor, setApplyToExistingMentor] = useState(true);
  const [applyToExistingScholar, setApplyToExistingScholar] = useState(true);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const flowSearchInputRef = useRef<HTMLInputElement | null>(null);
  const tabParam = searchParams.get('tab');
  const cohortParam = searchParams.get('cohortId');
  const activeTab = COHORT_TOOL_TABS.includes(tabParam as CohortToolTab)
    ? (tabParam as CohortToolTab)
    : 'cohorts';

  const selectedCohortMemberIds = useMemo(
    () => new Set(cohortMembers.map((member) => member.user.id)),
    [cohortMembers],
  );
  const roleOptions = useMemo<Array<{ value: CohortRole; label: string }>>(
    () => [
      { value: 'mentee', label: getCohortRoleLabel('mentee', cohortDraft) },
      { value: 'mentor', label: getCohortRoleLabel('mentor', cohortDraft) },
      { value: 'cohort_director', label: 'Cohort Director' },
      { value: 'cohort_admin', label: 'Cohort Admin' },
    ],
    [cohortDraft],
  );
  const isViewingAsPlatformAdmin = useMemo(
    () =>
      Boolean(
        currentUser?.is_staff &&
          currentUser.id &&
          selectedCohortId &&
          !cohortMembers.some((member) => String(member.user.id) === String(currentUser.id)),
      ),
    [cohortMembers, currentUser?.id, currentUser?.is_staff, selectedCohortId],
  );

  const loadTemplates = useCallback(async () => {
    setLoadingTemplates(true);
    try {
      const response = await adminContent.checklistItemTemplates.list();
      setTemplates(normalizeList<ChecklistItemTemplate>(response.data));
    } catch (error) {
      console.error('Failed to load checklist item templates', error);
      addToast({
        title: 'Error',
        description: 'Unable to load checklist item templates.',
        variant: 'destructive',
      });
    } finally {
      setLoadingTemplates(false);
    }
  }, [addToast]);

  const loadGroups = useCallback(async () => {
    setLoadingGroups(true);
    try {
      const response = await adminContent.checklistItemTemplateGroups.list();
      setGroups(normalizeList<ChecklistItemTemplateGroup>(response.data));
    } catch (error) {
      console.error('Failed to load checklist item template groups', error);
      addToast({
        title: 'Error',
        description: 'Unable to load checklist item template groups.',
        variant: 'destructive',
      });
    } finally {
      setLoadingGroups(false);
    }
  }, [addToast]);

  const loadCohorts = useCallback(async () => {
    try {
      const response = await adminContent.cohorts.list();
      setCohorts(normalizeList<CohortSummary>(response.data));
    } catch (error) {
      console.error('Failed to load cohorts', error);
      addToast({
        title: 'Error',
        description: 'Unable to load cohorts.',
        variant: 'destructive',
      });
    }
  }, [addToast]);

  const loadOrganizations = useCallback(async () => {
    try {
      const response = await adminContent.organizations.list({ is_active: true });
      setOrganizations(normalizeList<OrganizationSummary>(response.data));
    } catch (error) {
      console.error('Failed to load organizations', error);
      addToast({
        title: 'Error',
        description: 'Unable to load organizations.',
        variant: 'destructive',
      });
    }
  }, [addToast]);

  const loadFlows = useCallback(async () => {
    const requestId = ++flowsRequestRef.current;
    setLoadingFlows(true);
    try {
      const summaries: FlowSummary[] = [];
      let page = 1;
      let hasNextPage: boolean;
      do {
        const response = await adminContent.flows.listSummary({ is_active: true, page_size: 100, page });
        if (requestId !== flowsRequestRef.current) return;
        summaries.push(...response.data.results);
        hasNextPage = Boolean(response.data.next);
        page += 1;
      } while (hasNextPage);
      setFlows(summaries);
    } catch (error) {
      if (requestId !== flowsRequestRef.current) return;
      console.error('Failed to load flows', error);
      addToast({
        title: 'Error',
        description: 'Unable to load modules.',
        variant: 'destructive',
      });
    } finally {
      if (requestId === flowsRequestRef.current) setLoadingFlows(false);
    }
  }, [addToast]);

  useEffect(() => {
    void loadTemplates();
    void loadGroups();
    void loadCohorts();
    void loadOrganizations();
    void loadFlows();
    return () => { flowsRequestRef.current += 1; };
  }, [loadTemplates, loadGroups, loadCohorts, loadOrganizations, loadFlows]);

  useEffect(() => {
    if (!isFlowSelectOpen) return;
    if (!flowSearchInputRef.current) return;
    if (document.activeElement === flowSearchInputRef.current) return;
    const focusSearch = () => flowSearchInputRef.current?.focus();
    const frame = requestAnimationFrame(focusSearch);
    return () => cancelAnimationFrame(frame);
  }, [isFlowSelectOpen, flowSearchTerm]);

  const filteredTemplates = useMemo(() => {
    if (!templateSearchTerm.trim()) {
      return templates.filter((template) => template.is_active);
    }
    const query = templateSearchTerm.trim().toLowerCase();
    return templates.filter(
      (template) =>
        template.is_active && template.title.toLowerCase().includes(query),
    );
  }, [templateSearchTerm, templates]);

  const activeGroups = useMemo(
    () => groups.filter((group) => group.is_active),
    [groups],
  );

  const filteredGroups = useMemo(() => {
    if (!groupSearchTerm.trim()) {
      return activeGroups;
    }
    const query = groupSearchTerm.trim().toLowerCase();
    return activeGroups.filter((group) => group.name.toLowerCase().includes(query));
  }, [activeGroups, groupSearchTerm]);

  const filteredFlows = useMemo(() => {
    if (!flowSearchTerm.trim()) {
      return flows;
    }
    const query = flowSearchTerm.trim().toLowerCase();
    return flows.filter((flow) => flow.name.toLowerCase().includes(query));
  }, [flows, flowSearchTerm]);

  const activeCohorts = useMemo(
    () => cohorts.filter((cohort) => cohort.is_active),
    [cohorts],
  );
  const activeOrganizations = useMemo(
    () => organizations.filter((organization) => organization.is_active !== false),
    [organizations],
  );
  const sortedActiveOrganizations = useMemo(
    () => [...activeOrganizations].sort((a, b) => a.name.localeCompare(b.name)),
    [activeOrganizations],
  );
  const filteredCohorts = useMemo(() => {
    const query = cohortSearchTerm.trim().toLowerCase();

    return [...activeCohorts]
      .sort((a, b) => {
        const orgNameA = a.organization?.name ?? '';
        const orgNameB = b.organization?.name ?? '';
        return orgNameA.localeCompare(orgNameB) || a.name.localeCompare(b.name);
      })
      .filter((cohort) => {
        if (
          cohortOrganizationFilter !== ALL_ORGANIZATIONS_VALUE &&
          cohort.organization?.id !== cohortOrganizationFilter
        ) {
          return false;
        }

        if (!query) {
          return true;
        }

        const organizationName = cohort.organization?.name?.toLowerCase() ?? '';
        const organizationCode = cohort.organization?.org_id?.toLowerCase() ?? '';
        return (
          cohort.name.toLowerCase().includes(query) ||
          organizationName.includes(query) ||
          organizationCode.includes(query)
        );
      });
  }, [activeCohorts, cohortOrganizationFilter, cohortSearchTerm]);

  const defaultCoursesDirty = useMemo(() => {
    const serverIds = (cohortContentDefaults.course_assignments ?? []).map((assignment) => assignment.course.id);
    const draftIds = draftDefaultCourses.map((assignment) => assignment.course_id);
    return JSON.stringify(serverIds) !== JSON.stringify(draftIds);
  }, [cohortContentDefaults.course_assignments, draftDefaultCourses]);

  const defaultFlowsDirty = useMemo(() => {
    const serverIds = (cohortContentDefaults.flow_assignments ?? []).map((assignment) => assignment.flow.id);
    const draftIds = draftDefaultFlows.map((assignment) => assignment.flow_id);
    if (serverIds.length !== draftIds.length) return true;
    const draftSet = new Set(draftIds);
    return serverIds.some((id) => !draftSet.has(id));
  }, [cohortContentDefaults.flow_assignments, draftDefaultFlows]);

  const handleSelectTemplate = useCallback((template: ChecklistItemTemplate | null) => {
    if (!template) {
      setSelectedTemplateId(null);
      setTemplateDraft(EMPTY_TEMPLATE);
      return;
    }
    setSelectedTemplateId(template.id);
    setTemplateDraft(templateToDraft(template));
  }, []);

  const handleSelectGroup = useCallback((group: ChecklistItemTemplateGroup | null) => {
    if (!group) {
      setSelectedGroupId(null);
      setGroupDraft(EMPTY_GROUP);
      return;
    }
    setSelectedGroupId(group.id);
    setGroupDraft(groupToDraft(group));
  }, []);

  const loadCohortMembers = useCallback(
    async (cohortId: string) => {
      try {
        const response = await adminContent.cohorts.listMembers(cohortId);
        setCohortMembers(response.data as CohortMember[]);
      } catch (error) {
        console.error('Failed to load cohort members', error);
        addToast({
          title: 'Error',
          description: 'Unable to load cohort members.',
          variant: 'destructive',
        });
      }
    },
    [addToast],
  );

  const loadCohortAssignments = useCallback(
    async (cohortId: string) => {
      try {
        const response = await adminContent.cohorts.getAssignments(cohortId);
        setCohortAssignments(response.data as CohortChecklistAssignments);
      } catch (error) {
        console.error('Failed to load cohort assignments', error);
        addToast({
          title: 'Error',
          description: 'Unable to load cohort checklist assignments.',
          variant: 'destructive',
        });
      }
    },
    [addToast],
  );

  const applyCohortContentDefaultsResponse = useCallback((data: ContentDefaultsResponse) => {
    setCohortContentDefaults(data);
    setDraftDefaultCourses(
      (data.course_assignments ?? []).map((assignment) => ({
        course_id: assignment.course.id,
        name: assignment.course.name,
      })),
    );
    setDraftDefaultFlows(
      (data.flow_assignments ?? []).map((assignment) => ({
        flow_id: assignment.flow.id,
        name: assignment.flow.name,
      })),
    );
    setDraftInspireDeckIds((data.inspire_deck_assignments ?? []).map((assignment) => assignment.deck.id));
  }, []);

  const loadInspireDeckOptions = useCallback(async (organizationId?: string | null) => {
    try {
      const requests = [adminContent.inspireDecks.list('global')];
      if (organizationId) requests.push(adminContent.inspireDecks.list(organizationId));
      const responses = await Promise.all(requests);
      const decks = responses.flatMap((response) => normalizeList<AdminInspireDeck>(response.data));
      setAvailableInspireDecks(decks.filter((deck, index, all) => all.findIndex((item) => item.id === deck.id) === index));
    } catch {
      setAvailableInspireDecks([]);
    }
  }, []);

  const moveDraftInspireDeck = useCallback((deckId: string, direction: -1 | 1) => {
    setDraftInspireDeckIds((current) => {
      const index = current.indexOf(deckId);
      const nextIndex = index + direction;
      if (index < 0 || nextIndex < 0 || nextIndex >= current.length) return current;
      const next = [...current];
      [next[index], next[nextIndex]] = [next[nextIndex], next[index]];
      return next;
    });
  }, []);

  const loadCohortContentDefaults = useCallback(
    async (cohortId: string) => {
      setIsLoadingCohortContentDefaults(true);
      try {
        const response = await adminContent.cohorts.getContentDefaults(cohortId);
        applyCohortContentDefaultsResponse(response.data as ContentDefaultsResponse);
      } catch (error) {
        console.error('Failed to load cohort content defaults', error);
        addToast({
          title: 'Error',
          description: 'Unable to load cohort content defaults.',
          variant: 'destructive',
        });
        applyCohortContentDefaultsResponse(EMPTY_CONTENT_DEFAULTS);
      } finally {
        setIsLoadingCohortContentDefaults(false);
      }
    },
    [addToast, applyCohortContentDefaultsResponse],
  );

  const handleTabChange = useCallback(
    (value: string) => {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.set('tab', value);
      setSearchParams(nextParams, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const handleSelectCohort = useCallback(
    (cohort: CohortSummary | null) => {
      cohortDateRangeValidationActiveRef.current = false;
      setCohortDateErrors(EMPTY_COHORT_DATE_ERRORS);
      const nextParams = new URLSearchParams(searchParams);
      nextParams.set('tab', 'cohorts');

      if (!cohort) {
        selectedCohortIdRef.current = null;
        setSelectedCohortId(null);
        setCohortDraft(EMPTY_COHORT);
        setCohortMembers([]);
        setCohortAssignments(EMPTY_ASSIGNMENTS);
        applyCohortContentDefaultsResponse(EMPTY_CONTENT_DEFAULTS);
        setUserSearchResults([]);
        setUserSearchTerm('');
        nextParams.delete('cohortId');
        setSearchParams(nextParams, { replace: true });
        return;
      }
      selectedCohortIdRef.current = cohort.id;
      setSelectedCohortId(cohort.id);
      setCohortDraft(cohortToDraft(cohort));
      void loadCohortMembers(cohort.id);
      void loadCohortAssignments(cohort.id);
      void loadCohortContentDefaults(cohort.id);
      void loadInspireDeckOptions(cohort.organization?.id);
      setUserSearchResults([]);
      setUserSearchTerm('');
      nextParams.set('cohortId', cohort.id);
      setSearchParams(nextParams, { replace: true });
    },
    [
      applyCohortContentDefaultsResponse,
      loadCohortAssignments,
      loadCohortContentDefaults,
      loadCohortMembers,
      loadInspireDeckOptions,
      searchParams,
      setSearchParams,
    ],
  );

  useEffect(() => {
    if (activeTab !== 'cohorts' || !cohortParam || selectedCohortIdRef.current === cohortParam) {
      return;
    }
    const cohort = activeCohorts.find((entry) => entry.id === cohortParam);
    if (cohort) {
      handleSelectCohort(cohort);
    }
  }, [activeCohorts, activeTab, cohortParam, handleSelectCohort]);

  const handleCohortDraftChange = useCallback(
    (field: keyof CohortDraft, value: string | boolean) => {
      setCohortDraft((prev) => ({ ...prev, [field]: value }));
      if (field === 'start_date' || field === 'end_date') {
        const startDate = field === 'start_date' ? String(value) : cohortDraft.start_date;
        const endDate = field === 'end_date' ? String(value) : cohortDraft.end_date;
        setCohortDateErrors((currentErrors) => {
          const nextErrors: CohortDateErrors = {
            ...currentErrors,
            [field]: null,
          };
          if (!cohortDateRangeValidationActiveRef.current) return nextErrors;

          const rangeError = getCohortDateValidationError(startDate, endDate);
          if (rangeError) {
            if (!nextErrors.end_date || nextErrors.end_date.source === 'range') {
              nextErrors.end_date = { message: rangeError, source: 'range' };
            }
          } else if (nextErrors.end_date?.source === 'range') {
            nextErrors.end_date = null;
          }
          return nextErrors;
        });
      }
    },
    [cohortDraft.end_date, cohortDraft.start_date],
  );

  const handleTemplateDraftChange = useCallback(
    (field: keyof TemplateDraft, value: string | boolean) => {
      setTemplateDraft((prev) => ({ ...prev, [field]: value }));
    },
    [],
  );

  const handleTemplateExternalUrlChange = useCallback((value: string) => {
    setTemplateDraft((prev) => ({
      ...prev,
      external_url: value,
      ...(value.trim()
        ? {
            question_flow_id: NO_FLOW_VALUE,
            media: null,
            media_asset_id: null,
          }
        : {}),
    }));
  }, []);

  const handleTemplateFlowChange = useCallback((value: string) => {
    setTemplateDraft((prev) => ({
      ...prev,
      question_flow_id: value,
      ...(value !== NO_FLOW_VALUE
        ? {
            external_url: '',
            media: null,
            media_asset_id: null,
          }
        : {}),
    }));
  }, []);

  const handleGroupDraftChange = useCallback(
    (field: keyof GroupDraft, value: string | string[]) => {
      setGroupDraft((prev) => ({ ...prev, [field]: value }));
    },
    [],
  );

  const handleSaveCohort = useCallback(async () => {
    if (!cohortDraft.name.trim()) {
      addToast({ title: 'Name required', description: 'Enter a cohort name.' });
      return;
    }
    if (!cohortDraft.org_id.trim()) {
      addToast({ title: 'Organization required', description: 'Select an organization for this cohort.' });
      return;
    }
    if (
      !cohortDraft.mentee_label_singular.trim() ||
      !cohortDraft.mentee_label_plural.trim() ||
      !cohortDraft.mentor_label_singular.trim() ||
      !cohortDraft.mentor_label_plural.trim()
    ) {
      addToast({ title: 'Role labels required', description: 'Enter singular and plural labels for both roles.' });
      return;
    }
    const dateValidationError = getCohortDateValidationError(
      cohortDraft.start_date,
      cohortDraft.end_date,
    );
    if (dateValidationError) {
      cohortDateRangeValidationActiveRef.current = true;
      setCohortDateErrors((currentErrors) => ({
        ...currentErrors,
        end_date: { message: dateValidationError, source: 'range' },
      }));
      addToast({
        title: 'Invalid cohort dates',
        description: dateValidationError,
        variant: 'destructive',
      });
      return;
    }
    setIsSavingCohort(true);
    const payload = {
      name: cohortDraft.name.trim(),
      description: cohortDraft.description ?? '',
      org_id: cohortDraft.org_id.trim(),
      mentee_label_singular: cohortDraft.mentee_label_singular.trim(),
      mentee_label_plural: cohortDraft.mentee_label_plural.trim(),
      mentor_label_singular: cohortDraft.mentor_label_singular.trim(),
      mentor_label_plural: cohortDraft.mentor_label_plural.trim(),
      start_date: cohortDraft.start_date || null,
      end_date: cohortDraft.end_date || null,
      is_active: cohortDraft.is_active,
    };
    try {
      let savedCohort: CohortSummary;
      if (cohortDraft.id) {
        const response = await adminContent.cohorts.update(cohortDraft.id, payload);
        savedCohort = response.data as CohortSummary;
        addToast({ title: 'Cohort updated' });
      } else {
        const response = await adminContent.cohorts.create(payload);
        savedCohort = response.data as CohortSummary;
        addToast({ title: 'Cohort created' });
      }

      selectedCohortIdRef.current = savedCohort.id;
      setSelectedCohortId(savedCohort.id);
      setCohortDraft(cohortToDraft(savedCohort));
      cohortDateRangeValidationActiveRef.current = false;
      setCohortDateErrors(EMPTY_COHORT_DATE_ERRORS);
      await Promise.all([
        loadCohortMembers(savedCohort.id),
        loadCohortAssignments(savedCohort.id),
        loadCohortContentDefaults(savedCohort.id),
        loadInspireDeckOptions(savedCohort.organization?.id || cohortDraft.org_id),
      ]);
      await loadCohorts();
    } catch (error) {
      console.error('Failed to save cohort', error);
      const startDateFieldError = extractApiFieldErrorMessage(error, ['start_date']);
      const endDateFieldError = extractApiFieldErrorMessage(error, ['end_date']);
      if (startDateFieldError || endDateFieldError) {
        setCohortDateErrors({
          start_date: startDateFieldError
            ? { message: startDateFieldError, source: 'server' }
            : null,
          end_date: endDateFieldError
            ? { message: endDateFieldError, source: 'server' }
            : null,
        });
        return;
      }
      addToast({
        title: 'Error',
        description: extractApiErrorMessage(error, 'Unable to save cohort.'),
        variant: 'destructive',
      });
    } finally {
      setIsSavingCohort(false);
    }
  }, [
    addToast,
    cohortDraft,
    loadCohortAssignments,
    loadCohortContentDefaults,
    loadCohortMembers,
    loadCohorts,
    loadInspireDeckOptions,
  ]);

  const handleDeleteCohort = useCallback(async () => {
    if (!cohortDraft.id) return;
    const confirmDelete = window.confirm('Delete this cohort?');
    if (!confirmDelete) return;
    setIsSavingCohort(true);
    try {
      await adminContent.cohorts.delete(cohortDraft.id);
      addToast({ title: 'Cohort deleted' });
      await loadCohorts();
      setSelectedCohortId(null);
      setCohortDraft(EMPTY_COHORT);
      cohortDateRangeValidationActiveRef.current = false;
      setCohortDateErrors(EMPTY_COHORT_DATE_ERRORS);
      setCohortMembers([]);
      setCohortAssignments(EMPTY_ASSIGNMENTS);
      applyCohortContentDefaultsResponse(EMPTY_CONTENT_DEFAULTS);
    } catch (error) {
      console.error('Failed to delete cohort', error);
      addToast({
        title: 'Error',
        description: 'Unable to delete cohort.',
        variant: 'destructive',
      });
    } finally {
      setIsSavingCohort(false);
    }
  }, [addToast, applyCohortContentDefaultsResponse, cohortDraft.id, loadCohorts]);

  const handleUserSearch = useCallback(async () => {
    if (!selectedCohortId) return;
    const query = userSearchTerm.trim();
    if (!query) {
      setUserSearchResults([]);
      return;
    }
    setUserSearchLoading(true);
    try {
      const response = await adminContent.cohorts.userSearch({
        search: query,
        cohort_id: selectedCohortId,
      });
      setUserSearchResults(response.data as AdminUserSummary[]);
    } catch (error) {
      console.error('Failed to search users', error);
      addToast({
        title: 'Error',
        description: 'Unable to search users.',
        variant: 'destructive',
      });
    } finally {
      setUserSearchLoading(false);
    }
  }, [addToast, selectedCohortId, userSearchTerm]);

  const handleAddMember = useCallback(
    async (userId: string) => {
      if (!selectedCohortId) return;
      try {
        const response = await adminContent.cohorts.updateMembers(selectedCohortId, {
          members: [{ user_id: userId, role: selectedUserRole }],
        });
        const result = response.data as CohortMemberWriteResponse;
        await loadCohortMembers(selectedCohortId);
        if (result.created > 0) {
          addToast({ title: 'Member added' });
          return;
        }
        if (result.updated > 0) {
          addToast({
            title: 'Member already in cohort',
            description: 'No new membership was created.',
          });
          return;
        }
        if (result.skipped.length > 0) {
          addToast({
            title: 'Unable to add member',
            description: result.skipped[0]?.reason ?? 'The user could not be added to this cohort.',
            variant: 'destructive',
          });
          return;
        }
        addToast({
          title: 'Unable to add member',
          description: 'The user could not be added to this cohort.',
          variant: 'destructive',
        });
      } catch (error) {
        console.error('Failed to add member', error);
        addToast({
          title: 'Error',
          description: 'Unable to add member.',
          variant: 'destructive',
        });
      }
    },
    [addToast, loadCohortMembers, selectedCohortId, selectedUserRole],
  );

  const handleStaffJoinCohort = useCallback(async () => {
    if (!selectedCohortId || !currentUser?.id) return;
    try {
      const response = await adminContent.cohorts.updateMembers(selectedCohortId, {
        members: [
          {
            user_id: String(currentUser.id),
            role: staffJoinRole,
            is_current: staffJoinAsCurrent,
          },
        ],
      });
      const result = response.data as CohortMemberWriteResponse;
      await loadCohortMembers(selectedCohortId);
      if (result.skipped.length > 0) {
        addToast({
          title: 'Unable to join cohort',
          description: result.skipped[0]?.reason ?? 'The membership could not be created.',
          variant: 'destructive',
        });
        return;
      }
      addToast({ title: 'Joined cohort as member' });
    } catch (error) {
      console.error('Failed to join cohort as member', error);
      addToast({
        title: 'Error',
        description: 'Unable to join cohort as member.',
        variant: 'destructive',
      });
    }
  }, [
    addToast,
    currentUser?.id,
    loadCohortMembers,
    selectedCohortId,
    staffJoinAsCurrent,
    staffJoinRole,
  ]);

  const handleMakeCurrentMember = useCallback(
    async (member: CohortMember) => {
      if (!selectedCohortId) return;
      try {
        await adminContent.cohorts.updateMembers(selectedCohortId, {
          members: [
            {
              user_id: member.user.id,
              role: member.role,
              mentor_id: member.mentor_id ?? null,
              is_current: true,
            },
          ],
        });
        await loadCohortMembers(selectedCohortId);
        addToast({ title: 'Current cohort updated' });
      } catch (error) {
        console.error('Failed to update current cohort member', error);
        addToast({
          title: 'Error',
          description: 'Unable to update the current cohort for this user.',
          variant: 'destructive',
        });
      }
    },
    [addToast, loadCohortMembers, selectedCohortId],
  );

  const handleRemoveMember = useCallback(
    async (userId: string) => {
      if (!selectedCohortId) return;
      try {
        await adminContent.cohorts.removeMember(selectedCohortId, userId);
        await loadCohortMembers(selectedCohortId);
      } catch (error) {
        console.error('Failed to remove member', error);
        addToast({
          title: 'Error',
          description: 'Unable to remove member.',
          variant: 'destructive',
        });
      }
    },
    [addToast, loadCohortMembers, selectedCohortId],
  );

  const handleUpdateMemberRole = useCallback(
    async (userId: string, role: CohortRole) => {
      if (!selectedCohortId) return;
      try {
        await adminContent.cohorts.updateMembers(selectedCohortId, {
          members: [{ user_id: userId, role }],
        });
        await loadCohortMembers(selectedCohortId);
      } catch (error) {
        console.error('Failed to update member role', error);
        addToast({
          title: 'Error',
          description: 'Unable to update member role.',
          variant: 'destructive',
        });
      }
    },
    [addToast, loadCohortMembers, selectedCohortId],
  );

  const handleSaveDefaultCourses = useCallback(async () => {
    if (!selectedCohortId) return;
    setIsSavingDefaultCourses(true);
    try {
      const response = await adminContent.cohorts.updateContentDefaults(selectedCohortId, {
        course_ids: draftDefaultCourses.map((assignment) => assignment.course_id),
      });
      applyCohortContentDefaultsResponse(response.data as ContentDefaultsResponse);
      addToast({ title: 'Cohort course defaults saved' });
    } catch (error) {
      console.error('Failed to save cohort course defaults', error);
      addToast({
        title: 'Error',
        description: 'Unable to save cohort course defaults.',
        variant: 'destructive',
      });
    } finally {
      setIsSavingDefaultCourses(false);
    }
  }, [addToast, applyCohortContentDefaultsResponse, draftDefaultCourses, selectedCohortId]);

  const handleSaveDefaultFlows = useCallback(async () => {
    if (!selectedCohortId) return;
    setIsSavingDefaultFlows(true);
    try {
      const response = await adminContent.cohorts.updateContentDefaults(selectedCohortId, {
        flow_ids: draftDefaultFlows.map((assignment) => assignment.flow_id),
      });
      applyCohortContentDefaultsResponse(response.data as ContentDefaultsResponse);
      addToast({ title: 'Cohort module defaults saved' });
    } catch (error) {
      console.error('Failed to save cohort flow defaults', error);
      addToast({
        title: 'Error',
        description: 'Unable to save cohort module defaults.',
        variant: 'destructive',
      });
    } finally {
      setIsSavingDefaultFlows(false);
    }
  }, [addToast, applyCohortContentDefaultsResponse, draftDefaultFlows, selectedCohortId]);

  const handleOverrideToggle = useCallback(
    async (field: 'override_org_course_defaults' | 'override_org_flow_defaults' | 'override_org_inspire_defaults', value: boolean) => {
      if (!selectedCohortId) return;
      setIsSavingDefaultOverrides(true);
      try {
        const response = await adminContent.cohorts.updateContentDefaults(selectedCohortId, {
          [field]: value,
        });
        applyCohortContentDefaultsResponse(response.data as ContentDefaultsResponse);
      } catch (error) {
        console.error('Failed to update cohort default override', error);
        addToast({
          title: 'Error',
          description: 'Unable to update cohort default override.',
          variant: 'destructive',
        });
      } finally {
        setIsSavingDefaultOverrides(false);
      }
    },
    [addToast, applyCohortContentDefaultsResponse, selectedCohortId],
  );

  const handleSaveInspireDefaults = useCallback(async () => {
    if (!selectedCohortId) return;
    setIsSavingInspireDefaults(true);
    try {
      const response = await adminContent.cohorts.updateContentDefaults(selectedCohortId, {
        inspire_deck_ids: draftInspireDeckIds,
      });
      applyCohortContentDefaultsResponse(response.data as ContentDefaultsResponse);
      addToast({ title: 'Cohort Inspire defaults saved' });
    } catch (error) {
      addToast({ title: 'Error', description: 'Unable to save cohort Inspire defaults.', variant: 'destructive' });
    } finally {
      setIsSavingInspireDefaults(false);
    }
  }, [addToast, applyCohortContentDefaultsResponse, draftInspireDeckIds, selectedCohortId]);

  const toggleAssignment = useCallback(
    (role: AssignmentRole, type: 'template' | 'group', id: string) => {
      setCohortAssignments((prev) => {
        const roleAssignments = prev[role];
        const key = type === 'template' ? 'template_ids' : 'template_group_ids';
        const list = roleAssignments[key];
        const exists = list.includes(id);
        const nextList = exists ? list.filter((item) => item !== id) : [...list, id];
        return {
          ...prev,
          [role]: {
            ...roleAssignments,
            [key]: nextList,
          },
        };
      });
    },
    [],
  );

  const handleSaveAssignments = useCallback(
    async (role: AssignmentRole) => {
      if (!selectedCohortId) return;
      const payload = {
        role,
        template_ids: cohortAssignments[role].template_ids,
        template_group_ids: cohortAssignments[role].template_group_ids,
        apply_to_existing: role === 'mentor' ? applyToExistingMentor : applyToExistingScholar,
      };
      try {
        await adminContent.cohorts.updateAssignments(selectedCohortId, payload);
        addToast({ title: 'Assignments updated' });
      } catch (error) {
        console.error('Failed to update assignments', error);
        addToast({
          title: 'Error',
          description: 'Unable to update assignments.',
          variant: 'destructive',
        });
      }
    },
    [
      addToast,
      applyToExistingMentor,
      applyToExistingScholar,
      cohortAssignments,
      selectedCohortId,
    ],
  );

  const handleSaveTemplate = useCallback(async () => {
    if (!templateDraft.title.trim()) {
      addToast({ title: 'Title required', description: 'Enter a template title.' });
      return;
    }
    const hasFlow = templateDraft.question_flow_id !== NO_FLOW_VALUE;
    const hasUrl = Boolean(templateDraft.external_url.trim());
    const hasMedia = Boolean(templateDraft.media_asset_id);
    if ([hasFlow, hasUrl, hasMedia].filter(Boolean).length > 1) {
      addToast({
        title: 'Choose one option',
        description: 'Checklist templates can only include one of: module, external URL, or media.',
        variant: 'destructive',
      });
      return;
    }
    setIsSavingTemplate(true);
    const parsedOffset = Number(templateDraft.due_offset_days);
    const parsedFlowId = Number(templateDraft.question_flow_id);
    const payload = {
      title: templateDraft.title.trim(),
      description: templateDraft.description ?? '',
      due_offset_days:
        templateDraft.due_offset_days.trim() && Number.isFinite(parsedOffset)
          ? parsedOffset
          : null,
      external_url: normalizeExternalUrl(templateDraft.external_url),
      is_active: templateDraft.is_active,
      media_asset_id: templateDraft.media_asset_id ?? null,
      question_flow:
        templateDraft.question_flow_id !== NO_FLOW_VALUE && Number.isFinite(parsedFlowId)
          ? parsedFlowId
          : null,
    };
    try {
      if (templateDraft.id) {
        await adminContent.checklistItemTemplates.update(templateDraft.id, payload);
        addToast({ title: 'Template updated' });
      } else {
        await adminContent.checklistItemTemplates.create(payload);
        addToast({ title: 'Template created' });
      }
      await loadTemplates();
      await loadGroups();
      setSelectedTemplateId(null);
      setTemplateDraft(EMPTY_TEMPLATE);
    } catch (error) {
      console.error('Failed to save template', error);
      const message = extractApiErrorMessage(error, 'Unable to save template.');
      addToast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    } finally {
      setIsSavingTemplate(false);
    }
  }, [addToast, loadGroups, loadTemplates, templateDraft]);

  const handleUploadClick = () => {
    setMediaError('');
    fileInputRef.current?.click();
  };

  const handleUploadChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setMediaError('');
    setUploadingMedia(true);
    try {
      const arrayBuffer = await file.arrayBuffer();
      const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      const hashHex = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');

      const lookup = await media.lookup(hashHex);
      if (lookup.found && lookup.assetId && lookup.key) {
        const assetId = lookup.assetId as string;
        const storageKey = lookup.key as string;
        setTemplateDraft((prev) => ({
          ...prev,
          media_asset_id: assetId ?? null,
          media: {
            id: assetId,
            storage_key: storageKey,
            media_type: toMediaType(lookup.mediaType),
            mime_type: lookup.mimeType ?? null,
            is_active: true,
            url: undefined,
          },
          external_url: '',
          question_flow_id: NO_FLOW_VALUE,
        }));
        return;
      }

      const { key, assetId, mediaType, mimeType } = await media.upload(file, {
        visibility: 'private',
        folder: 'checklist-item-templates',
        hash: hashHex,
      });

      if (!assetId) {
        throw new Error('Upload succeeded without an asset id.');
      }
      const resolvedAssetId = assetId;

      setTemplateDraft((prev) => ({
        ...prev,
        media_asset_id: resolvedAssetId ?? null,
        media: {
          id: resolvedAssetId,
          storage_key: key,
          media_type: toMediaType(mediaType),
          mime_type: mimeType ?? null,
          is_active: true,
          url: undefined,
        },
        external_url: '',
        question_flow_id: NO_FLOW_VALUE,
      }));
    } catch (err: any) {
      const message = err?.response?.data?.error || err?.message || 'Failed to upload media.';
      setMediaError(message);
    } finally {
      setUploadingMedia(false);
      if (event.target) event.target.value = '';
    }
  };

  const handleRemoveMedia = () => {
    setTemplateDraft((prev) => ({
      ...prev,
      media_asset_id: null,
      media: null,
    }));
  };

  const handleDeleteTemplate = useCallback(async () => {
    if (!templateDraft.id) return;
    const confirmDelete = window.confirm('Delete this template?');
    if (!confirmDelete) return;
    setIsSavingTemplate(true);
    try {
      await adminContent.checklistItemTemplates.delete(templateDraft.id);
      addToast({ title: 'Template deleted' });
      await loadTemplates();
      await loadGroups();
      setSelectedTemplateId(null);
      setTemplateDraft(EMPTY_TEMPLATE);
    } catch (error) {
      console.error('Failed to delete template', error);
      addToast({
        title: 'Error',
        description: 'Unable to delete template.',
        variant: 'destructive',
      });
    } finally {
      setIsSavingTemplate(false);
    }
  }, [addToast, loadGroups, loadTemplates, templateDraft.id]);

  const handleSaveGroup = useCallback(async () => {
    if (!groupDraft.name.trim()) {
      addToast({ title: 'Name required', description: 'Enter a group name.' });
      return;
    }
    setIsSavingGroup(true);
    const payload = {
      name: groupDraft.name.trim(),
      description: groupDraft.description ?? '',
      template_ids: groupDraft.template_ids,
    };
    try {
      if (groupDraft.id) {
        await adminContent.checklistItemTemplateGroups.update(groupDraft.id, payload);
        addToast({ title: 'Group updated' });
      } else {
        await adminContent.checklistItemTemplateGroups.create(payload);
        addToast({ title: 'Group created' });
      }
      await loadGroups();
      setSelectedGroupId(null);
      setGroupDraft(EMPTY_GROUP);
    } catch (error) {
      console.error('Failed to save group', error);
      addToast({
        title: 'Error',
        description: 'Unable to save group.',
        variant: 'destructive',
      });
    } finally {
      setIsSavingGroup(false);
    }
  }, [addToast, groupDraft, loadGroups]);

  const handleDeleteGroup = useCallback(async () => {
    if (!groupDraft.id) return;
    const confirmDelete = window.confirm('Delete this group?');
    if (!confirmDelete) return;
    setIsSavingGroup(true);
    try {
      await adminContent.checklistItemTemplateGroups.delete(groupDraft.id);
      addToast({ title: 'Group deleted' });
      await loadGroups();
      setSelectedGroupId(null);
      setGroupDraft(EMPTY_GROUP);
    } catch (error) {
      console.error('Failed to delete group', error);
      addToast({
        title: 'Error',
        description: 'Unable to delete group.',
        variant: 'destructive',
      });
    } finally {
      setIsSavingGroup(false);
    }
  }, [addToast, groupDraft.id, loadGroups]);

  const handleToggleTemplate = useCallback((templateId: string) => {
    setGroupDraft((prev) => {
      const exists = prev.template_ids.includes(templateId);
      if (exists) {
        return { ...prev, template_ids: prev.template_ids.filter((id) => id !== templateId) };
      }
      return { ...prev, template_ids: [...prev.template_ids, templateId] };
    });
  }, []);

  return (
    <div className="space-y-6 px-4 py-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold text-foreground">Cohorts</h1>
          <p className="text-muted-foreground">
            Manage cohorts, members, checklist templates, and cohort-specific content defaults.
          </p>
        </div>
        <div className="flex gap-2">
          <Button size="auto" variant="secondary" onClick={() => loadTemplates()}>
            Refresh Templates
          </Button>
          <Button size="auto" variant="secondary" onClick={() => loadGroups()}>
            Refresh Groups
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList className="gap-2 px-6">
          <TabsTrigger value="cohorts">Cohorts</TabsTrigger>
          <TabsTrigger value="templates">Checklist Templates</TabsTrigger>
          <TabsTrigger value="groups">Template Groups</TabsTrigger>
        </TabsList>

      <TabsContent value="templates">
          <div className="grid gap-6 lg:grid-cols-[420px_minmax(0,1fr)]">
            <Card className="w-full max-w-none" maxWidth="none">
              <CardHeader>
                <CardTitle>Checklist Templates</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 items-stretch">
                <Button
                  size="auto"
                  onClick={() => handleSelectTemplate(null)}
                  variant="secondary"
                >
                  New Template
                </Button>
                <Input
                  value={templateSearchTerm}
                  onChange={(event) => setTemplateSearchTerm(event.target.value)}
                  placeholder="Search templates..."
                />
                {loadingTemplates ? (
                  <div
                    data-testid="mentorship-templates-loading"
                    className="flex min-h-24 items-center justify-center text-sm text-muted-foreground"
                  >
                    <LoadingAnimation size="sm" />
                  </div>
                ) : (
                  <div className="space-y-2">
                    {filteredTemplates.length === 0 ? (
                      <p className="text-sm text-muted-foreground">No templates yet.</p>
                    ) : (
                      filteredTemplates.map((template) => (
                        <button
                          key={template.id}
                          type="button"
                          onClick={() => handleSelectTemplate(template)}
                          className={cn(
                            'w-full rounded-lg border px-3 py-2 text-left text-sm transition',
                            selectedTemplateId === template.id
                              ? 'border-foundation-orange-500 bg-foundation-orange-50'
                              : 'border-border hover:border-foundation-orange-300',
                          )}
                        >
                          <div className="font-semibold">{template.title}</div>
                          <div className="text-xs text-muted-foreground">
                            {template.is_active ? 'Active' : 'Inactive'}
                            {template.question_flow_name ? ` • Module: ${template.question_flow_name}` : ''}
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="w-full max-w-none" maxWidth="none">
              <CardHeader>
                <CardTitle>
                  {templateDraft.id ? 'Edit Template' : 'Create Template'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 items-stretch">
                <div className="space-y-2">
                  <Label htmlFor="template-title">Checklist Template Title</Label>
                  <Input
                    id="template-title"
                    value={templateDraft.title}
                    onChange={(event) => handleTemplateDraftChange('title', event.target.value)}
                    placeholder="Checklist template title"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="template-description">Description</Label>
                  <Textarea
                    id="template-description"
                    value={templateDraft.description}
                    onChange={(event) =>
                      handleTemplateDraftChange('description', event.target.value)
                    }
                    placeholder="Optional details"
                  />
                </div>
                <div className="">
                  <div className="space-y-2">
                    <Label htmlFor="template-offset">Due Offset (days)</Label>
                    <Input
                      id="template-offset"
                      type="number"
                      value={templateDraft.due_offset_days}
                      onChange={(event) =>
                        handleTemplateDraftChange('due_offset_days', event.target.value)
                      }
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="template-url">External URL</Label>
                  <Input
                    id="template-url"
                    value={templateDraft.external_url}
                    onChange={(event) => handleTemplateExternalUrlChange(event.target.value)}
                    placeholder="https://..."
                  />
                </div>
                <div className="space-y-2">
                  <Label>Module</Label>
                  <Select
                    value={templateDraft.question_flow_id}
                    onValueChange={handleTemplateFlowChange}
                    onOpenChange={setIsFlowSelectOpen}
                    disabled={loadingFlows}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a module (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <div className="p-2">
                        <Input
                          ref={flowSearchInputRef}
                          value={flowSearchTerm}
                          onChange={(event) => setFlowSearchTerm(event.target.value)}
                          onKeyDown={(event) => event.stopPropagation()}
                          placeholder="Search modules..."
                        />
                      </div>
                      <SelectItem value={NO_FLOW_VALUE}>No module</SelectItem>
                      {filteredFlows.map((flow) => (
                        <SelectItem key={flow.id} value={String(flow.id)}>
                          {flow.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {loadingFlows ? (
                    <p className="text-xs text-muted-foreground">Loading modules...</p>
                  ) : null}
                </div>
                <div className="space-y-2">
                  <Label>Media upload</Label>
                  <div className="rounded-lg border border-border p-3">
                    <input
                      ref={fileInputRef}
                      type="file"
                      className="hidden"
                      onChange={handleUploadChange}
                    />
                    <div className="flex flex-wrap items-center gap-3">
                      <Button
                        size="auto"
                        variant="secondary"
                        onClick={handleUploadClick}
                        disabled={uploadingMedia}
                      >
                        {uploadingMedia ? 'Uploading...' : 'Upload media'}
                      </Button>
                      {templateDraft.media ? (
                        <div className="flex items-center gap-2 rounded-md border border-border bg-muted/40 px-3 py-2 text-sm">
                          <span className="font-medium">
                            {templateDraft.media.storage_key.split('/').pop() ||
                              templateDraft.media.id}
                          </span>
                          <Button
                            size="auto"
                            variant="secondary"
                            onClick={handleRemoveMedia}
                          >
                            Remove
                          </Button>
                        </div>
                      ) : (
                        <span className="text-sm text-muted-foreground">
                          No media attached.
                        </span>
                      )}
                    </div>
                    {mediaError ? (
                      <p className="mt-2 text-xs text-destructive">{mediaError}</p>
                    ) : null}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button size="auto" onClick={handleSaveTemplate} disabled={isSavingTemplate}>
                    {isSavingTemplate ? 'Saving...' : 'Save Template'}
                  </Button>
                  {templateDraft.id ? (
                    <Button
                      size="auto"
                      variant="secondary"
                      onClick={handleDeleteTemplate}
                      disabled={isSavingTemplate}
                    >
                      Delete
                    </Button>
                  ) : null}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="groups">
          <div className="grid gap-6 lg:grid-cols-[420px_minmax(0,1fr)]">
            <Card className="w-full max-w-none" maxWidth="none">
              <CardHeader>
                <CardTitle>Template Groups</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 items-stretch">
                <Button size="auto" onClick={() => handleSelectGroup(null)} variant="secondary">
                  New Group
                </Button>
                {loadingGroups ? (
                  <div
                    data-testid="mentorship-groups-loading"
                    className="flex min-h-24 items-center justify-center text-sm text-muted-foreground"
                  >
                    <LoadingAnimation size="sm" />
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Input
                      value={groupSearchTerm}
                      onChange={(event) => setGroupSearchTerm(event.target.value)}
                      placeholder="Search groups..."
                    />
                    {filteredGroups.length === 0 ? (
                      <p className="text-sm text-muted-foreground">No groups yet.</p>
                    ) : (
                      filteredGroups.map((group) => (
                        <button
                          key={group.id}
                          type="button"
                          onClick={() => handleSelectGroup(group)}
                          className={cn(
                            'w-full rounded-lg border px-3 py-2 text-left text-sm transition',
                            selectedGroupId === group.id
                              ? 'border-foundation-orange-500 bg-foundation-orange-50'
                              : 'border-border hover:border-foundation-orange-300',
                          )}
                        >
                          <div className="font-semibold">{group.name}</div>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="w-full max-w-none" maxWidth="none">
              <CardHeader>
                <CardTitle>{groupDraft.id ? 'Edit Group' : 'Create Group'}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 items-stretch">
                <div className="space-y-2">
                  <Label htmlFor="group-name">Name</Label>
                  <Input
                    id="group-name"
                    value={groupDraft.name}
                    onChange={(event) => handleGroupDraftChange('name', event.target.value)}
                    placeholder="Group name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="group-description">Description</Label>
                  <Textarea
                    id="group-description"
                    value={groupDraft.description}
                    onChange={(event) =>
                      handleGroupDraftChange('description', event.target.value)
                    }
                    placeholder="Optional description"
                  />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <Label>Available Templates</Label>
                    <span className="text-xs text-muted-foreground">
                      Selected: {groupDraft.template_ids.length}
                    </span>
                  </div>
                  <div className="max-h-64 space-y-2 overflow-y-auto rounded-lg border p-3">
                    {loadingTemplates ? (
                      <div
                        data-testid="mentorship-group-templates-loading"
                        className="flex min-h-24 items-center justify-center text-sm text-muted-foreground"
                      >
                        <LoadingAnimation size="sm" />
                      </div>
                    ) : templates.length === 0 ? (
                      <p className="text-sm text-muted-foreground">No templates available.</p>
                    ) : (
                      templates.map((template) => {
                        const checked = groupDraft.template_ids.includes(template.id);
                        return (
                          <label
                            key={template.id}
                            className="flex items-center gap-2 text-sm"
                          >
                            <Checkbox
                              checked={checked}
                              onCheckedChange={() => handleToggleTemplate(template.id)}
                            />
                            <span className="flex-1">{template.title}</span>
                          </label>
                        );
                      })
                    )}
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  <Button size="auto" onClick={handleSaveGroup} disabled={isSavingGroup}>
                    {isSavingGroup ? 'Saving...' : 'Save Group'}
                  </Button>
                  {groupDraft.id ? (
                    <Button
                      size="auto"
                      variant="secondary"
                      onClick={handleDeleteGroup}
                      disabled={isSavingGroup}
                    >
                      Delete
                    </Button>
                  ) : null}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="cohorts">
          <div className="grid gap-6 lg:grid-cols-[420px_minmax(0,1fr)]">
            <Card className="w-full max-w-none" maxWidth="none">
              <CardHeader>
                <CardTitle>Cohorts</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 items-stretch">
                <Button size="auto" onClick={() => handleSelectCohort(null)} variant="secondary">
                  New Cohort
                </Button>
                <div className="grid gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="cohort-search">Search Cohorts</Label>
                    <Input
                      id="cohort-search"
                      value={cohortSearchTerm}
                      onChange={(event) => setCohortSearchTerm(event.target.value)}
                      placeholder="Search by cohort or organization"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="cohort-organization-filter">Filter by Organization</Label>
                    <Select
                      value={cohortOrganizationFilter}
                      onValueChange={setCohortOrganizationFilter}
                    >
                      <SelectTrigger id="cohort-organization-filter">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={ALL_ORGANIZATIONS_VALUE}>All organizations</SelectItem>
                        {sortedActiveOrganizations.map((organization) => (
                          <SelectItem key={organization.id} value={organization.id}>
                            {organization.name} ({organization.org_id})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {filteredCohorts.length} cohort{filteredCohorts.length === 1 ? '' : 's'} shown
                  </p>
                </div>
                {activeCohorts.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No cohorts yet.</p>
                ) : filteredCohorts.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    No cohorts match the current search or organization filter.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {filteredCohorts.map((cohort) => (
                      <button
                        key={cohort.id}
                        type="button"
                        onClick={() => handleSelectCohort(cohort)}
                        className={cn(
                          'w-full rounded-lg border px-3 py-2 text-left text-sm transition',
                          selectedCohortId === cohort.id
                            ? 'border-foundation-orange-500 bg-foundation-orange-50'
                            : 'border-border hover:border-foundation-orange-300',
                        )}
                      >
                        <div className="font-semibold">{cohort.name}</div>
                        <div className="text-xs text-muted-foreground">
                          {cohort.organization?.name || 'No organization'}
                          {cohort.organization?.org_id ? ` (${cohort.organization.org_id})` : ''}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="w-full max-w-none" maxWidth="none">
              <CardHeader>
                <CardTitle>{cohortDraft.id ? 'Edit Cohort' : 'Create Cohort'}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 items-stretch">
                <div className="space-y-2">
                  <Label htmlFor="cohort-name">Name</Label>
                  <Input
                    id="cohort-name"
                    value={cohortDraft.name}
                    onChange={(event) => handleCohortDraftChange('name', event.target.value)}
                    placeholder="Cohort name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cohort-org-id">Organization</Label>
                  <Select
                    value={cohortDraft.org_id}
                    onValueChange={(value) => handleCohortDraftChange('org_id', value)}
                  >
                    <SelectTrigger id="cohort-org-id">
                      <SelectValue placeholder="Select an organization" />
                    </SelectTrigger>
                    <SelectContent>
                      {sortedActiveOrganizations.map((organization) => (
                        <SelectItem key={organization.id} value={organization.org_id}>
                          {organization.name} ({organization.org_id})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {activeOrganizations.length === 0 ? (
                    <p className="text-xs text-muted-foreground">
                      No active organizations available. Create one in Admin Organizations first.
                    </p>
                  ) : null}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cohort-description">Description</Label>
                  <Textarea
                    id="cohort-description"
                    value={cohortDraft.description}
                    onChange={(event) =>
                      handleCohortDraftChange('description', event.target.value)
                    }
                    placeholder="Optional description"
                  />
                </div>
                <div className="space-y-3 rounded-lg border border-border p-4">
                  <div>
                    <p className="text-sm font-semibold">Role labels</p>
                    <p className="text-xs text-muted-foreground">
                      Customize how this cohort refers to its member roles.
                    </p>
                  </div>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="cohort-mentee-label-singular">Scholar singular</Label>
                      <Input
                        id="cohort-mentee-label-singular"
                        value={cohortDraft.mentee_label_singular}
                        onChange={(event) =>
                          handleCohortDraftChange('mentee_label_singular', event.target.value)
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="cohort-mentee-label-plural">Scholar plural</Label>
                      <Input
                        id="cohort-mentee-label-plural"
                        value={cohortDraft.mentee_label_plural}
                        onChange={(event) =>
                          handleCohortDraftChange('mentee_label_plural', event.target.value)
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="cohort-mentor-label-singular">Mentor singular</Label>
                      <Input
                        id="cohort-mentor-label-singular"
                        value={cohortDraft.mentor_label_singular}
                        onChange={(event) =>
                          handleCohortDraftChange('mentor_label_singular', event.target.value)
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="cohort-mentor-label-plural">Mentor plural</Label>
                      <Input
                        id="cohort-mentor-label-plural"
                        value={cohortDraft.mentor_label_plural}
                        onChange={(event) =>
                          handleCohortDraftChange('mentor_label_plural', event.target.value)
                        }
                      />
                    </div>
                  </div>
                </div>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="cohort-start">Start Date</Label>
                    <Input
                      id="cohort-start"
                      type="date"
                      value={cohortDraft.start_date}
                      onChange={(event) =>
                        handleCohortDraftChange('start_date', event.target.value)
                      }
                      aria-invalid={Boolean(cohortDateErrors.start_date)}
                      aria-describedby={
                        cohortDateErrors.start_date ? 'cohort-start-date-error' : undefined
                      }
                    />
                    {cohortDateErrors.start_date ? (
                      <p
                        id="cohort-start-date-error"
                        role="alert"
                        className="text-sm text-destructive"
                      >
                        {cohortDateErrors.start_date.message}
                      </p>
                    ) : null}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="cohort-end">End Date</Label>
                    <Input
                      id="cohort-end"
                      type="date"
                      value={cohortDraft.end_date}
                      onChange={(event) =>
                        handleCohortDraftChange('end_date', event.target.value)
                      }
                      aria-invalid={Boolean(cohortDateErrors.end_date)}
                      aria-describedby={
                        cohortDateErrors.end_date ? 'cohort-end-date-error' : undefined
                      }
                    />
                    {cohortDateErrors.end_date ? (
                      <p
                        id="cohort-end-date-error"
                        role="alert"
                        className="text-sm text-destructive"
                      >
                        {cohortDateErrors.end_date.message}
                      </p>
                    ) : null}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button size="auto" onClick={handleSaveCohort} disabled={isSavingCohort}>
                    {isSavingCohort ? 'Saving...' : 'Save Cohort'}
                  </Button>
                  {cohortDraft.id ? (
                    <>
                      <Button size="auto" variant="secondary" asChild>
                        <Link to={`/mentorship/director?cohortId=${cohortDraft.id}`}>View Dashboard</Link>
                      </Button>
                      <Button
                        size="auto"
                        variant="secondary"
                        onClick={handleDeleteCohort}
                        disabled={isSavingCohort}
                      >
                        Delete
                      </Button>
                    </>
                  ) : null}
                </div>
              </CardContent>
            </Card>
          </div>

          {selectedCohortId ? (
            <div className="mt-6 space-y-6">
              {isViewingAsPlatformAdmin ? (
                <div className="rounded-lg border border-foundation-orange-300 bg-foundation-orange-50 px-4 py-4">
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
                        <UserCheck className="h-4 w-4" />
                        Viewing as platform admin
                      </div>
                      <div className="text-xs text-muted-foreground">Not listed as a cohort member.</div>
                    </div>
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                      <Select
                        value={staffJoinRole}
                        onValueChange={(value) => setStaffJoinRole(value as CohortRole)}
                      >
                        <SelectTrigger className="w-full sm:w-[12rem]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {roleOptions.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <label className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Checkbox
                          checked={staffJoinAsCurrent}
                          onCheckedChange={(checked) => setStaffJoinAsCurrent(Boolean(checked))}
                        />
                        Make current
                      </label>
                      <Button type="button" size="auto" onClick={handleStaffJoinCohort}>
                        Join cohort as member
                      </Button>
                    </div>
                  </div>
                </div>
              ) : null}

              <Card className="w-full max-w-none" maxWidth="none">
                <CardHeader>
                  <CardTitle>Default Content Assignments</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6 items-stretch">
                  <div className="grid gap-4 md:grid-cols-3">
                    <div className="space-y-3 rounded-lg border border-border p-4">
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <p className="text-sm font-semibold">Course Override</p>
                          <p className="text-xs text-muted-foreground">
                            When enabled, this cohort&apos;s course defaults replace organization course defaults.
                          </p>
                        </div>
                        <Checkbox
                          checked={Boolean(cohortContentDefaults.override_org_course_defaults)}
                          onCheckedChange={(checked) =>
                            void handleOverrideToggle('override_org_course_defaults', Boolean(checked))
                          }
                          disabled={isSavingDefaultOverrides}
                        />
                      </div>
                    </div>
                    <div className="space-y-3 rounded-lg border border-border p-4">
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <p className="text-sm font-semibold">Module Override</p>
                          <p className="text-xs text-muted-foreground">
                            When enabled, this cohort&apos;s standalone module defaults replace organization module defaults.
                          </p>
                        </div>
                        <Checkbox
                          checked={Boolean(cohortContentDefaults.override_org_flow_defaults)}
                          onCheckedChange={(checked) =>
                            void handleOverrideToggle('override_org_flow_defaults', Boolean(checked))
                          }
                          disabled={isSavingDefaultOverrides}
                        />
                      </div>
                    </div>
                    <div className="space-y-3 rounded-lg border border-border p-4">
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <p className="text-sm font-semibold">Inspire Deck Override</p>
                          <p className="text-xs text-muted-foreground">When enabled, this cohort&apos;s Inspire decks replace organization defaults. An empty selection means no cards.</p>
                        </div>
                        <Checkbox
                          checked={Boolean(cohortContentDefaults.override_org_inspire_defaults)}
                          onCheckedChange={(checked) => void handleOverrideToggle('override_org_inspire_defaults', Boolean(checked))}
                          disabled={isSavingDefaultOverrides}
                        />
                      </div>
                    </div>
                  </div>

                  {isLoadingCohortContentDefaults ? (
                    <div
                      data-testid="mentorship-cohort-content-defaults-loading"
                      className="flex min-h-24 items-center justify-center text-sm text-muted-foreground"
                    >
                      <LoadingAnimation size="sm" />
                    </div>
                  ) : (
                    <>
                      {Boolean(cohortContentDefaults.override_org_course_defaults) ||
                      Boolean(cohortContentDefaults.override_org_flow_defaults) ? (
                        <ContentAssignmentEditor
                          showCourses={Boolean(cohortContentDefaults.override_org_course_defaults)}
                          showFlows={Boolean(cohortContentDefaults.override_org_flow_defaults)}
                          courseTitle="Cohort Default Course Assignments"
                          flowTitle="Cohort Default Standalone Module Assignments"
                          flowDescription="Save selections here, then use the override toggles above to decide whether this cohort should inherit org defaults or use its own course and module defaults."
                          courseEmptyMessage="No cohort default courses selected."
                          flowEmptyMessage="No cohort default standalone modules selected."
                          courses={draftDefaultCourses}
                          flows={draftDefaultFlows}
                          onCoursesChange={setDraftDefaultCourses}
                          onFlowsChange={setDraftDefaultFlows}
                          onSaveCourses={handleSaveDefaultCourses}
                          onSaveFlows={handleSaveDefaultFlows}
                          coursesDirty={defaultCoursesDirty}
                          flowsDirty={defaultFlowsDirty}
                          savingCourses={isSavingDefaultCourses}
                          savingFlows={isSavingDefaultFlows}
                        />
                      ) : (
                        <p className="text-sm text-muted-foreground">
                          Enable an override to edit cohort-specific default assignments.
                        </p>
                      )}
                      {Boolean(cohortContentDefaults.override_org_inspire_defaults) && (
                        <div className="space-y-3 border-t border-border pt-5">
                          <div><p className="text-sm font-semibold">Cohort Inspire Decks</p><p className="text-xs text-muted-foreground">These ordered choices replace organization Inspire decks for this cohort.</p></div>
                          {availableInspireDecks.length === 0 ? <p className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">No eligible decks are available.</p> : availableInspireDecks.map((deck) => (
                            <label key={deck.id} className="flex items-start gap-3 rounded-lg border border-border bg-white p-3">
                              <Checkbox checked={draftInspireDeckIds.includes(deck.id)} onCheckedChange={(checked) => setDraftInspireDeckIds((current) => Boolean(checked) ? [...current, deck.id] : current.filter((id) => id !== deck.id))} />
                              <span><span className="block text-sm font-medium">{deck.name}</span><span className="block text-xs text-muted-foreground">{deck.owner_organization?.name || 'WiseFriend'} · {deck.status}</span></span>
                            </label>
                          ))}
                          {draftInspireDeckIds.length > 0 && (
                            <div className="space-y-2 rounded-lg border border-border bg-muted/20 p-3">
                              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Meeting order</p>
                              {draftInspireDeckIds.map((deckId, index) => {
                                const deck = availableInspireDecks.find((item) => item.id === deckId);
                                if (!deck) return null;
                                return (
                                  <div key={deckId} className="flex items-center gap-2 rounded-md bg-white px-3 py-2">
                                    <span className="min-w-0 flex-1 truncate text-sm font-medium">{index + 1}. {deck.name}</span>
                                    <Button type="button" size="icon" variant="ghost" aria-label={`Move ${deck.name} up`} disabled={index === 0} onClick={() => moveDraftInspireDeck(deckId, -1)}><ArrowUp className="h-4 w-4" /></Button>
                                    <Button type="button" size="icon" variant="ghost" aria-label={`Move ${deck.name} down`} disabled={index === draftInspireDeckIds.length - 1} onClick={() => moveDraftInspireDeck(deckId, 1)}><ArrowDown className="h-4 w-4" /></Button>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                          <Button type="button" size="auto" onClick={handleSaveInspireDefaults} disabled={isSavingInspireDefaults}>{isSavingInspireDefaults ? 'Saving…' : 'Save Inspire decks'}</Button>
                        </div>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>

              <div className="grid gap-6 lg:grid-cols-2">
                <Card className="w-full max-w-none" maxWidth="none">
                  <CardHeader>
                    <CardTitle>Cohort Members</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4 items-stretch">
                    <div className="space-y-2">
                      <Label htmlFor="user-search">Add Members</Label>
                      <div className="flex flex-wrap gap-2">
                        <Input
                          id="user-search"
                          value={userSearchTerm}
                          onChange={(event) => setUserSearchTerm(event.target.value)}
                          placeholder="Search by name or email"
                        />
                        <Select
                          value={selectedUserRole}
                          onValueChange={(value) => setSelectedUserRole(value as CohortRole)}
                        >
                          <SelectTrigger className="w-full sm:w-[11.25rem]">
                            <SelectValue placeholder="Role" />
                          </SelectTrigger>
                          <SelectContent>
                            {roleOptions.map((option) => (
                              <SelectItem key={option.value} value={option.value}>
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button
                          size="auto"
                          variant="secondary"
                          onClick={handleUserSearch}
                          disabled={userSearchLoading}
                        >
                          {userSearchLoading ? 'Searching...' : 'Search'}
                        </Button>
                      </div>
                    </div>
                    <div className="space-y-2">
                      {userSearchResults.length === 0 ? (
                        <p className="text-sm text-muted-foreground">
                          Search for users to add to this cohort.
                        </p>
                      ) : (
                        userSearchResults.map((user) => {
                          const isAlreadyInCohort = selectedCohortMemberIds.has(user.id);
                          return (
                            <div
                              key={user.id}
                              className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border p-2"
                            >
                              <div className="text-sm">
                                <div className="font-semibold">
                                  {user.first_name || user.last_name
                                    ? `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim()
                                    : user.email}
                                </div>
                                <div className="text-xs text-muted-foreground">{user.email}</div>
                                {isAlreadyInCohort ? (
                                  <div className="text-xs text-muted-foreground">Already in this cohort</div>
                                ) : null}
                              </div>
                              <Button
                                size="auto"
                                onClick={() => handleAddMember(user.id)}
                                disabled={isAlreadyInCohort}
                              >
                                {isAlreadyInCohort ? 'Added' : 'Add'}
                              </Button>
                            </div>
                          );
                        })
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label>Current Members</Label>
                      {cohortMembers.length === 0 ? (
                        <p className="text-sm text-muted-foreground">No members yet.</p>
                      ) : (
                        <div className="space-y-2">
                          {cohortMembers.map((member) => (
                            <div
                              key={member.id}
                              className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border p-2"
                            >
                              <div className="text-sm">
                                <div className="flex items-center gap-2 font-semibold">
                                  {member.user.first_name || member.user.last_name
                                    ? `${member.user.first_name ?? ''} ${member.user.last_name ?? ''}`.trim()
                                    : member.user.email}
                                  {member.is_current ? (
                                    <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-primary">
                                      Current
                                    </span>
                                  ) : null}
                                </div>
                                <div className="text-xs text-muted-foreground">{member.user.email}</div>
                              </div>
                              <div className="flex flex-wrap items-center gap-2">
                                <Select
                                  value={member.role}
                                  onValueChange={(value) =>
                                    handleUpdateMemberRole(member.user.id, value as CohortRole)
                                  }
                                >
                                  <SelectTrigger className="w-full sm:w-[11.25rem]">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {roleOptions.map((option) => (
                                      <SelectItem key={option.value} value={option.value}>
                                        {option.label}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                {!member.is_current ? (
                                  <Button
                                    size="auto"
                                    variant="secondary"
                                    onClick={() => handleMakeCurrentMember(member)}
                                  >
                                    Make Current
                                  </Button>
                                ) : null}
                                <Button
                                  size="auto"
                                  variant="secondary"
                                  onClick={() => handleRemoveMember(member.user.id)}
                                >
                                  Remove
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                <Card className="w-full max-w-none" maxWidth="none">
                  <CardHeader>
                    <CardTitle>Checklist Assignments</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6 items-stretch">
                    {(['mentee', 'mentor'] as AssignmentRole[]).map((role) => (
                      <div key={role} className="space-y-3 rounded-lg border border-border p-3">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div>
                            <p className="text-sm font-semibold">
                              {getCohortRoleLabel(role, cohortDraft)} Assignments
                            </p>
                            <p className="text-xs text-muted-foreground">
                              Select checklist templates and groups for{' '}
                              {lowerLabel(getInviteeBucketLabel(role === 'mentee' ? 'scholars' : 'mentors', cohortDraft))}.
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Checkbox
                              checked={role === 'mentee' ? applyToExistingScholar : applyToExistingMentor}
                              onCheckedChange={(checked) =>
                                role === 'mentee'
                                  ? setApplyToExistingScholar(Boolean(checked))
                                  : setApplyToExistingMentor(Boolean(checked))
                              }
                            />
                            <span className="text-xs text-muted-foreground">Apply to existing members</span>
                          </div>
                        </div>

                        <div className="grid gap-4 md:grid-cols-2">
                          <div className="space-y-2">
                            <Label>Checklist Templates</Label>
                            <div className="max-h-48 space-y-2 overflow-y-auto rounded-lg border border-border p-3">
                              {filteredTemplates.length === 0 ? (
                                <p className="text-sm text-muted-foreground">No templates available.</p>
                              ) : (
                                filteredTemplates.map((template) => (
                                  <label key={template.id} className="flex items-center gap-2 text-sm">
                                    <Checkbox
                                      checked={cohortAssignments[role].template_ids.includes(template.id)}
                                      onCheckedChange={() =>
                                        toggleAssignment(role, 'template', template.id)
                                      }
                                    />
                                    <span className="flex-1">{template.title}</span>
                                  </label>
                                ))
                              )}
                            </div>
                          </div>
                          <div className="space-y-2">
                            <Label>Template Groups</Label>
                            <div className="max-h-48 space-y-2 overflow-y-auto rounded-lg border border-border p-3">
                              {activeGroups.length === 0 ? (
                                <p className="text-sm text-muted-foreground">No groups available.</p>
                              ) : (
                                activeGroups.map((group) => (
                                  <label key={group.id} className="flex items-center gap-2 text-sm">
                                    <Checkbox
                                      checked={cohortAssignments[role].template_group_ids.includes(group.id)}
                                      onCheckedChange={() => toggleAssignment(role, 'group', group.id)}
                                    />
                                    <span className="flex-1">{group.name}</span>
                                  </label>
                                ))
                              )}
                            </div>
                          </div>
                        </div>

                        <Button size="auto" onClick={() => handleSaveAssignments(role)}>
                          Save {getCohortRoleLabel(role, cohortDraft)} Assignments
                        </Button>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </div>
            </div>
          ) : (
            <p className="mt-6 text-sm text-muted-foreground">
              Select a cohort to manage members and checklist assignments.
            </p>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default MentorshipTools;
